import React, { useState, useEffect, useRef } from 'react';
import { Bell, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import NotificationPanel from './NotificationPanel';
import notificationService from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';

const NotificationBell = ({ isScrolled }) => {
  const { isAuthenticated, loading, userType } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [panelLoading, setPanelLoading] = useState(false);
  const bellRef = useRef(null);

  // Participants don't have notifications - don't render the bell at all
  const isParticipant = userType === 'participant';

  // Get the currently selected sponsor ID for scoping notifications
  const getSelectedSponsorId = () => {
    try {
      const raw = localStorage.getItem('selectedSponsor');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.id || null;
    } catch {
      return null;
    }
  };

  // Fetch unread count
  const fetchUnreadCount = async () => {
    if (isParticipant) return;
    try {
      const sponsorId = getSelectedSponsorId();
      const count = await notificationService.getUnreadCount(sponsorId);
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  // Fetch notifications when panel opens
  const fetchNotifications = async () => {
    if (panelLoading || isParticipant) return;

    try {
      setPanelLoading(true);
      const sponsorId = getSelectedSponsorId();
      // Dropdown shows the 10 most-recent via the purpose-built endpoint, so it's
      // decoupled from the full notifications page's pagination/page-size.
      const data = await notificationService.getRecentNotifications(sponsorId);
      setNotifications(Array.isArray(data) ? data : (data.results || []));
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setPanelLoading(false);
    }
  };

  // Track selected sponsor so we re-fetch when it changes
  const [currentSponsorId, setCurrentSponsorId] = useState(getSelectedSponsorId);
  const currentSponsorRef = useRef(currentSponsorId);

  // Listen for storage events (fired when sponsor changes) to immediately sync
  useEffect(() => {
    const handleStorageChange = () => {
      const latestSponsorId = getSelectedSponsorId();
      if (latestSponsorId !== currentSponsorRef.current) {
        currentSponsorRef.current = latestSponsorId;
        // Reset immediately so stale count from previous sponsor doesn't linger
        setUnreadCount(0);
        setNotifications([]);
        setCurrentSponsorId(latestSponsorId);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Initial load of unread count and polling – only when authenticated (not for participants)
  useEffect(() => {
    // While auth is still resolving, don't start polling yet
    if (loading) return;

    // Participants don't have notifications; skip on sponsor selector
    if (isParticipant || !isAuthenticated || location.pathname === '/sponsor-selector') {
      setUnreadCount(0);
      return;
    }

    // Fetch immediately when sponsor changes or on mount
    currentSponsorRef.current = currentSponsorId;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, loading, isParticipant, currentSponsorId]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bellRef.current && !bellRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBellClick = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  const handleMarkAsRead = async (notificationIds) => {
    try {
      await notificationService.markAsRead(notificationIds);
      await fetchNotifications();
      await fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const sponsorId = getSelectedSponsorId();
      await notificationService.markAllAsRead(sponsorId);
      await fetchNotifications();
      await fetchUnreadCount();
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
    }
  };

  // Don't render the notification bell for participants or on the sponsor selector
  if (isParticipant || location.pathname === '/sponsor-selector') {
    return null;
  }

  return (
    <div className="relative" ref={bellRef}>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleBellClick}
        className={`relative flex items-center justify-center p-2 rounded-full ${
          isScrolled 
            ? 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700' 
            : 'text-white hover:bg-white/10'
        } transition-all duration-200`}
        aria-label="Notifications"
      >
        <Bell size={20} strokeWidth={1.5} />
        
        {/* Unread count badge */}
        {unreadCount > 0 && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </motion.div>
        )}
      </motion.button>

      {/* Notification Panel */}
      <AnimatePresence mode="wait">
        {isOpen && (
          <NotificationPanel
            notifications={notifications}
            loading={panelLoading}
            onMarkAsRead={handleMarkAsRead}
            onMarkAllAsRead={handleMarkAllAsRead}
            onClose={() => setIsOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
